
import React, { useContext, useState } from "react"
import { maxBookingsOptions, maxBookingsRestrictedDaysOptions } from "@config"
import { CustomSwitch, Divider, ReadOnlyBadge, Required } from "@components"
import { NotificationContext, UserContext } from "@context"
import { hasExpired, mySqlToLocal } from "@helpers"
import { useUpdateEvent } from "@hooks"
import { useApolloClient } from "@apollo/client";
import { Button, Select, SelectItem } from "@heroui/react"

import PropTypes from "prop-types"

const EventSettings = ({ backButton, event }) => {
  const initialFormData = {
    vcAutoDisconnect: event.vc_auto_disconnect,
    maxBookingsPerStudent: event.max_bookings_per_student,
    maxBookingsRestrictedDays: event.max_bookings_restricted_days,
  }

  /**
   * State
   */
  const [form, setForm] = useState({ ...initialFormData });
  const [savedFormData, setSavedFormData] = useState({ ...initialFormData });

  /**
   * Context
   */
  const { setCurrentEvent } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const {updateEvent, loading} = useUpdateEvent()

  /**
   * Functions
   */
  const handleMaxBookingsChange = (value) => {
    setForm(prevForm => ({
      ...prevForm,
      maxBookingsPerStudent: value,
      maxBookingsRestrictedDays: value === 0 ? 0 : prevForm.maxBookingsRestrictedDays,
    }))
  }
 
  const handleSave = () => {
    updateEvent({ 
      variables: {
        input: {
          id: event.id,
          vc_auto_disconnect: form.vcAutoDisconnect,
          max_bookings_per_student: form.maxBookingsPerStudent,
          max_bookings_restricted_days: form.maxBookingsRestrictedDays,
        }
      },
      
      onCompleted: (response) => {
        notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true })
        setCurrentEvent(event.id) // forces user context to refresh event data
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session

        setSavedFormData({ ...form })
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: `An error occurred while updating the event.`, open: true })
      }
    })
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormData)
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  const isVideoConferenceEvent = event.meetingTypes.some(meetingType => meetingType.type === "VC")

  return (
    <div>
      <div className="flex justify-end">
        {isReadOnlyMode && <ReadOnlyBadge className="mb-4" isFullWidth size="lg" />}
      </div>

      <div className="mb-6">
        {isVideoConferenceEvent && (
          <React.Fragment>
            <CustomSwitch
              title="VC Auto Disconnect"
              isSelected={form.vcAutoDisconnect}
              handleChange={(isSelected) => setForm(prevForm => ({...prevForm, vcAutoDisconnect: isSelected}))}
              isDisabled={isReadOnlyMode}
            >
              <p>Automatically disconnect video calls when the slot end time has been reached.</p>
            </CustomSwitch>
            
            <Divider className="my-3" />
          </React.Fragment>
        )}

        <div className="flex items-center justify-between">
          <p className="font-semibold">Booking Limits</p>
        </div>
        
        <p className="mb-3 text-content2-foreground">Set the maximum number of bookings a guardian can make and how long the restriction applies.</p>
        
        <span className="flex items-center justify-between mb-1">
          <span className="mr-2 text-sm w-28">Limit<Required /></span>

          <Select 
            isRequired
            disallowEmptySelection={true}
            onChange={(event) => handleMaxBookingsChange(Number(event.target.value))}
            selectionMode="single"
            defaultSelectedKeys={[form.maxBookingsPerStudent.toString()]}
            aria-label="Max bookings per student"
            isDisabled={isReadOnlyMode}
          >
            {maxBookingsOptions.map((option) => (
              <SelectItem key={option.id.toString()} value={option.id}>{option.label}</SelectItem>
            ))}
          </Select>
        </span>

        <span className="flex items-center justify-between mb-1">
          <span className="mr-2 text-sm w-28">Duration<Required /></span>
            <Select
              key={`max-bookings-restricted-days-${form.maxBookingsPerStudent.toString()}`}
              isRequired
              disallowEmptySelection={true}
              onChange={(event) => setForm(prevForm => ({ ...prevForm, maxBookingsRestrictedDays: Number(event.target.value) }))}
              selectionMode="single"
              defaultSelectedKeys={[form.maxBookingsRestrictedDays.toString()]}
              aria-label="Max bookings days"
              isDisabled={form.maxBookingsPerStudent === 0 || isReadOnlyMode}
            >
            {maxBookingsRestrictedDaysOptions.map((option) => (
              <SelectItem key={option.id.toString()} value={option.id}>{option.label}</SelectItem>
            ))}
          </Select>
        </span>
      </div>
      
      <Button className="font-semibold" color="secondary" isDisabled={!isDirty} fullWidth onPress={() => handleSave()} isLoading={loading}>Save</Button>
      {backButton}
    </div>
  )
}

EventSettings.propTypes = {
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
    vc_auto_disconnect: PropTypes.bool.isRequired,
    max_bookings_per_student: PropTypes.number.isRequired,
    max_bookings_restricted_days: PropTypes.number.isRequired,
    meetingTypes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
}

export default EventSettings